import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AdminSidebar from "@/pages/admin/components/AdminSidebar";
import { getSupabase } from "@/lib/supabase";

interface HackerListing {
  id: number;
  profile_id: string;
  name: string;
  specialty: string | null;
  avatar: string | null;
  rating: number | null;
  rate: string | null;
  experience: string | null;
  age: number | null;
  gender: string | null;
  skills: string[];
  country: string | null;
  prizes: string[];
  certifications: string[];
  availability: string | null;
  ranking: number | null;
  verified: boolean;
  featured: boolean;
  created_at: string;
}

export default function AdminHackerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const hackerId = id ? parseInt(id, 10) : null;

  const [hacker, setHacker] = useState<HackerListing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!hackerId || isNaN(hackerId)) {
      setError("Invalid hacker ID");
      setLoading(false);
      return;
    }
    fetchHacker(hackerId);
  }, [hackerId]);

  const fetchHacker = async (hid: number) => {
    setLoading(true);
    setError(null);
    try {
      const supabase = getSupabase();
      const { data, error: supaError } = await supabase
        .from("hacker_listings")
        .select("*")
        .eq("id", hid)
        .maybeSingle();
      if (supaError) throw supaError;
      if (!data) {
        setError("Hacker not found");
      } else {
        setHacker(data as HackerListing);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load hacker");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#161b22]">
        <AdminSidebar />
        <div className="ml-60 flex items-center justify-center min-h-screen">
          <i className="fas fa-spinner fa-spin text-[#5cb85c] text-3xl" />
        </div>
      </div>
    );
  }

  if (error || !hacker) {
    return (
      <div className="min-h-screen bg-[#161b22]">
        <AdminSidebar />
        <div className="ml-60 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <i className="fas fa-exclamation-triangle text-5xl text-yellow-400 mb-4 block" />
            <h2 className="text-xl font-bold text-gray-200 mb-2">{error || "Hacker Not Found"}</h2>
            <Link
              to="/admin/hackers"
              className="inline-block bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-sm font-semibold px-6 py-2.5 rounded transition-all whitespace-nowrap"
            >
              <i className="fas fa-arrow-left mr-2" />Back to Hackers
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#161b22]">
      <AdminSidebar />
      <div className="ml-60">
        {/* Top bar */}
        <header className="bg-[#0d1117] border-b border-gray-700 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin/hackers")}
              className="w-8 h-8 rounded-lg hover:bg-[#1c2128] flex items-center justify-center text-gray-400 hover:text-white transition-all"
            >
              <i className="fas fa-arrow-left text-sm" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-200">Hacker Profile</h1>
              <p className="text-xs text-gray-500">ID #{hacker.id}</p>
            </div>
          </div>
          <Link
            to={`/hacker/${hacker.id}`}
            target="_blank"
            className="px-4 py-2 bg-[#1c2128] hover:bg-[#2c3e50] text-gray-300 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 whitespace-nowrap"
          >
            <i className="fas fa-external-link-alt text-[10px]" />View Public Page
          </Link>
        </header>

        <main className="p-6">
          {/* Profile Header Card */}
          <div className="bg-[#0d1117] rounded-xl border border-gray-800 p-6 md:p-8 mb-6">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              <div className="relative flex-shrink-0">
                {hacker.avatar ? (
                  <img
                    src={hacker.avatar}
                    alt={hacker.name}
                    className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover ring-4 ring-gray-700"
                  />
                ) : (
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-[#1c2128] ring-4 ring-gray-700 flex items-center justify-center">
                    <i className="fas fa-user-secret text-gray-500 text-3xl" />
                  </div>
                )}
                {hacker.featured && (
                  <span className="absolute -top-1 -right-1 bg-[#f0ad4e] text-black text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
                    Featured
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <h2 className="text-xl md:text-2xl font-bold text-white">{hacker.name}</h2>
                  {hacker.verified && (
                    <i className="fas fa-check-circle text-[#5bc0de] text-lg" title="Verified Hacker" />
                  )}
                  {hacker.verified && (
                    <span className="bg-[#5bc0de]/10 text-[#5bc0de] px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide">Verified</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-sm mb-3">
                  <span className="bg-[#5cb85c]/10 text-[#5cb85c] px-2.5 py-0.5 rounded text-xs font-semibold">{hacker.specialty || "—"}</span>
                  <span className="text-gray-400"><i className="fas fa-map-marker-alt mr-1" />{hacker.country || "—"}</span>
                  <span className="text-gray-400">
                    {hacker.age != null ? `${hacker.age} yrs` : "—"}
                    {hacker.gender ? ` · ${hacker.gender === "male" ? "Male" : "Female"}` : ""}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-1.5">
                    {Array.from({ length: 5 }).map((_, i) => {
                      const r = hacker.rating ?? 0;
                      const full = Math.floor(r);
                      const half = r % 1 >= 0.5;
                      if (i < full) return <i key={i} className="fas fa-star text-xs text-yellow-400" />;
                      if (i === full && half) return <i key={i} className="fas fa-star-half-alt text-xs text-yellow-400" />;
                      return <i key={i} className="far fa-star text-xs text-gray-600" />;
                    })}
                    <span className="text-sm font-bold text-yellow-400 ml-1">{(hacker.rating ?? 0).toFixed(1)}</span>
                  </div>
                  <span className="text-sm font-bold text-[#5cb85c]">{hacker.rate || "—"}</span>
                  <span className="text-sm text-gray-400">Rank #{hacker.ranking ?? 0}</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full uppercase ${hacker.availability === "Available" ? "bg-green-900/30 text-green-400" : hacker.availability === "Busy" ? "bg-yellow-900/30 text-yellow-400" : "bg-red-900/30 text-red-400"}`}>
                    {hacker.availability || "Unknown"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Info */}
              <div className="bg-[#0d1117] rounded-xl border border-gray-800 p-5 md:p-6">
                <h3 className="text-sm font-bold text-gray-200 uppercase tracking-wider mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InfoRow label="Full Name" value={hacker.name} />
                  <InfoRow label="Specialty" value={hacker.specialty} />
                  <InfoRow label="Experience" value={hacker.experience} />
                  <InfoRow label="Hourly Rate" value={hacker.rate} />
                  <InfoRow label="Country" value={hacker.country} />
                  <InfoRow label="Gender" value={hacker.gender ? hacker.gender.charAt(0).toUpperCase() + hacker.gender.slice(1) : "—"} />
                  <InfoRow label="Age" value={hacker.age ? `${hacker.age} years` : "—"} />
                  <InfoRow label="Ranking" value={`#${hacker.ranking}`} />
                  <InfoRow label="Verified" value={hacker.verified ? "Yes" : "No"} valueClass={hacker.verified ? "text-[#5bc0de] font-semibold" : "text-gray-500"} />
                  <InfoRow label="Featured" value={hacker.featured ? "Yes" : "No"} valueClass={hacker.featured ? "text-[#f0ad4e] font-semibold" : "text-gray-500"} />
                  <InfoRow label="Availability" value={hacker.availability || "—"} />
                  <InfoRow label="Profile ID" value={hacker.profile_id.slice(0, 8) + "..."} />
                </div>
              </div>

              {/* Skills */}
              {hacker.skills && hacker.skills.length > 0 && (
                <div className="bg-[#0d1117] rounded-xl border border-gray-800 p-5 md:p-6">
                  <h3 className="text-sm font-bold text-gray-200 uppercase tracking-wider mb-4">Skills & Expertise</h3>
                  <div className="flex flex-wrap gap-2">
                    {hacker.skills.map((skill) => (
                      <span key={skill} className="text-xs bg-[#5cb85c]/10 text-[#5cb85c] px-3 py-1.5 rounded-lg font-medium">{skill}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Certifications */}
              {hacker.certifications && hacker.certifications.length > 0 && (
                <div className="bg-[#0d1117] rounded-xl border border-gray-800 p-5 md:p-6">
                  <h3 className="text-sm font-bold text-gray-200 uppercase tracking-wider mb-4">Certifications</h3>
                  <div className="flex flex-wrap gap-2">
                    {hacker.certifications.map((cert) => (
                      <span key={cert} className="text-xs bg-[#5bc0de]/10 text-[#5bc0de] px-3 py-1.5 rounded-lg font-medium">{cert}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Prizes */}
              {hacker.prizes && hacker.prizes.length > 0 && (
                <div className="bg-[#0d1117] rounded-xl border border-gray-800 p-5 md:p-6">
                  <h3 className="text-sm font-bold text-gray-200 uppercase tracking-wider mb-4">Prizes & Awards</h3>
                  <ul className="space-y-2">
                    {hacker.prizes.map((prize, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-gray-400">
                        <i className="fas fa-trophy text-[#f0ad4e] mt-0.5 flex-shrink-0" />
                        <span>{prize}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Right column */}
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="bg-[#0d1117] rounded-xl border border-gray-800 p-5">
                <h3 className="text-sm font-bold text-gray-200 uppercase tracking-wider mb-4">Quick Stats</h3>
                <div className="space-y-3">
                  <StatRow icon="fas fa-star" label="Rating" value={(hacker.rating ?? 0).toFixed(1)} color="text-yellow-400" />
                  <StatRow icon="fas fa-trophy" label="Rank" value={`#${hacker.ranking ?? 0}`} color="text-gray-300" />
                  <StatRow icon="fas fa-dollar-sign" label="Rate" value={hacker.rate || "—"} color="text-[#5cb85c]" />
                  <StatRow icon="fas fa-calendar" label="Experience" value={hacker.experience || "—"} color="text-gray-300" />
                </div>
              </div>

              {/* Photo Preview */}
              {hacker.avatar && (
                <div className="bg-[#0d1117] rounded-xl border border-gray-800 p-5">
                  <h3 className="text-sm font-bold text-gray-200 uppercase tracking-wider mb-4">Photo</h3>
                  <img
                    src={hacker.avatar}
                    alt={hacker.name}
                    className="w-full aspect-square rounded-lg object-cover"
                  />
                </div>
              )}

              {/* Meta */}
              <div className="bg-[#0d1117] rounded-xl border border-gray-800 p-5">
                <h3 className="text-sm font-bold text-gray-200 uppercase tracking-wider mb-4">Meta</h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">ID</span>
                    <span className="text-gray-300 font-mono">{hacker.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Created</span>
                    <span className="text-gray-300">{new Date(hacker.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function InfoRow({ label, value, valueClass = "" }: { label: string; value: string | null; valueClass?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
      <span className={`text-sm text-gray-200 ${valueClass}`}>{value || "—"}</span>
    </div>
  );
}

function StatRow({ icon, label, value, color }: { icon: string; label: string; value: string; color: string }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <i className={`${icon} text-xs text-gray-500 w-4 text-center`} />
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <span className={`text-sm font-bold ${color}`}>{value}</span>
    </div>
  );
}